(() => {
  const BUFFER_SETS = 2;
  const TRANSITION_MS = 400;

  const initSetupCarousel = (root) => {
    if (root.dataset.nanoSetupReady === 'true') return;
    root.dataset.nanoSetupReady = 'true';

    const carousel = root.querySelector('[data-nano-setup-carousel]');
    const viewport = root.querySelector('[data-nano-setup-viewport]');
    const track = root.querySelector('[data-nano-setup-track]');
    const prevBtn = root.querySelector('[data-common-carousel-arrow="prev"]');
    const nextBtn = root.querySelector('[data-common-carousel-arrow="next"]');
    const dots = Array.from(root.querySelectorAll('[data-nano-setup-dot]'));

    if (!carousel || !viewport || !track) return;

    const sourceSlides = Array.from(track.querySelectorAll('[data-nano-setup-slide]'));
    if (sourceSlides.length === 0) return;

    const sourceCount = sourceSlides.length;
    let activeIndex = BUFFER_SETS * sourceCount;
    let isTransitioning = false;
    let isDragging = false;
    let hasDragged = false;
    let dragStartX = 0;
    let dragStartTrackX = 0;
    let autoplayTimer = null;
    let wheelAutoplayTimer = null;
    const DRAG_THRESHOLD = 24;
    const WHEEL_AUTOPLAY_DELAY = 1000;

    const getRealIndex = () => ((activeIndex % sourceCount) + sourceCount) % sourceCount;

    const updateDots = () => {
      const realIndex = getRealIndex();
      dots.forEach((dot, index) => {
        const active = index === realIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const makeSet = () => {
      const fragment = document.createDocumentFragment();

      sourceSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-nano-setup-slide');
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.nanoSetupClone = 'true';
        fragment.appendChild(clone);
      });

      return fragment;
    };

    for (let i = 0; i < BUFFER_SETS; i += 1) {
      track.insertBefore(makeSet(), track.firstChild);
    }

    for (let i = 0; i < BUFFER_SETS; i += 1) {
      track.appendChild(makeSet());
    }

    const slides = track.children;

    const getCurrentX = () => {
      const transform = window.getComputedStyle(track).transform;
      if (!transform || transform === 'none') return 0;
      return new DOMMatrix(transform).m41;
    };

    const getTargetX = () => {
      const slide = slides[activeIndex];
      if (!slide) return 0;

      const currentX = getCurrentX();
      const slideLeft = slide.getBoundingClientRect().left;
      const viewportLeft = viewport.getBoundingClientRect().left;
      const edgePad = parseFloat(window.getComputedStyle(viewport).paddingInlineStart) || 0;

      return currentX + viewportLeft + edgePad - slideLeft;
    };

    const getSlideTargetX = (slide) => {
      if (!slide) return 0;

      const currentX = getCurrentX();
      const slideLeft = slide.getBoundingClientRect().left;
      const viewportLeft = viewport.getBoundingClientRect().left;
      const edgePad = parseFloat(window.getComputedStyle(viewport).paddingInlineStart) || 0;

      return currentX + viewportLeft + edgePad - slideLeft;
    };

    const computeBounds = () => {
      const firstSlide = slides[0];
      const lastSlide = slides[slides.length - 1];

      if (!firstSlide || !lastSlide) return { minX: 0, maxX: 0 };

      const currentX = getCurrentX();
      const viewportRect = viewport.getBoundingClientRect();
      const edgePad = parseFloat(window.getComputedStyle(viewport).paddingInlineStart) || 0;
      const firstLeft = firstSlide.getBoundingClientRect().left;
      const lastRight = lastSlide.getBoundingClientRect().right;
      const maxX = currentX + viewportRect.left + edgePad - firstLeft;
      const minX = currentX + (viewportRect.right - edgePad) - lastRight;

      return { minX: Math.min(minX, maxX), maxX: Math.max(minX, maxX) };
    };

    const clampToBounds = (x, bounds = computeBounds()) =>
      Math.max(bounds.minX, Math.min(bounds.maxX, x));

    const getClosestSlideIndex = (x = getCurrentX()) => {
      let closestIndex = activeIndex;
      let closestDistance = Infinity;

      Array.from(slides).forEach((slide, index) => {
        const targetX = getSlideTargetX(slide);
        const distance = Math.abs(targetX - x);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const updatePositionState = (x = getCurrentX()) => {
      activeIndex = getClosestSlideIndex(x);
      updateDots();
    };

    const getDragLimit = () => {
      const slide = slides[activeIndex];
      return slide ? slide.getBoundingClientRect().width : viewport.clientWidth;
    };

    const clampDragDelta = (delta) => {
      const limit = getDragLimit();
      return Math.max(-limit, Math.min(limit, delta));
    };

    const applyPosition = ({ animate = true } = {}) => {
      const x = getTargetX();
      updateDots();

      if (!window.gsap) {
        track.style.transition = animate ? `transform ${TRANSITION_MS}ms ease` : 'none';
        track.style.transform = `translateX(${x}px)`;
        return;
      }

      window.gsap.to(track, {
        x,
        duration: animate ? TRANSITION_MS / 1000 : 0,
        ease: 'power2.out',
        overwrite: true,
      });
    };

    const setTrackX = (x) => {
      if (!window.gsap) {
        track.style.transition = 'none';
        track.style.transform = `translateX(${x}px)`;
        return;
      }

      window.gsap.set(track, { x });
    };

    const preserveVisualPosition = (mutate) => {
      const anchor = slides[activeIndex] || slides[0];
      const beforeLeft = anchor ? anchor.getBoundingClientRect().left : 0;
      const beforeX = getCurrentX();

      mutate();

      if (!anchor) return;
      const afterLeft = anchor.getBoundingClientRect().left;
      setTrackX(beforeX + beforeLeft - afterLeft);
    };

    const ensureBuffer = ({ preservePosition = false } = {}) => {
      const keepSets = BUFFER_SETS + 2;

      if (slides.length - 1 - activeIndex < BUFFER_SETS * sourceCount) {
        track.appendChild(makeSet());
      }

      if (activeIndex < BUFFER_SETS * sourceCount) {
        if (preservePosition) {
          preserveVisualPosition(() => track.insertBefore(makeSet(), track.firstChild));
        } else {
          track.insertBefore(makeSet(), track.firstChild);
        }
        activeIndex += sourceCount;
        if (!preservePosition) applyPosition({ animate: false });
      }

      if (slides.length - 1 - activeIndex > keepSets * sourceCount) {
        for (let i = 0; i < sourceCount; i += 1) track.removeChild(track.lastChild);
      }

      if (activeIndex > keepSets * sourceCount) {
        if (preservePosition) {
          preserveVisualPosition(() => {
            for (let i = 0; i < sourceCount; i += 1) track.removeChild(track.firstChild);
          });
        } else {
          for (let i = 0; i < sourceCount; i += 1) track.removeChild(track.firstChild);
        }
        activeIndex -= sourceCount;
        if (!preservePosition) applyPosition({ animate: false });
      }
    };

    const setActive = (index) => {
      if (isTransitioning) return;
      isTransitioning = true;
      activeIndex = index;
      updateDots();

      applyPosition({ animate: true });

      window.setTimeout(() => {
        isTransitioning = false;
        ensureBuffer();
      }, TRANSITION_MS + 40);
    };

    const autoplayEnabled = carousel.dataset.autoplay !== 'false';
    const autoplayInterval = parseInt(carousel.dataset.autoplayInterval || '4000', 10);

    const stopAutoplay = () => {
      window.clearTimeout(wheelAutoplayTimer);
      wheelAutoplayTimer = null;
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || sourceCount < 2) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => setActive(activeIndex + 1), autoplayInterval);
    };

    prevBtn?.addEventListener('click', () => {
      stopAutoplay();
      setActive(activeIndex - 1);
      startAutoplay();
    });

    nextBtn?.addEventListener('click', () => {
      stopAutoplay();
      setActive(activeIndex + 1);
      startAutoplay();
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const dotIndex = Number.parseInt(dot.dataset.nanoSetupDotIndex || '0', 10);
        const currentRealIndex = getRealIndex();
        const offset = dotIndex - currentRealIndex;
        if (offset === 0) return;

        stopAutoplay();
        setActive(activeIndex + offset);
        startAutoplay();
      });
    });

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (sourceCount < 2) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();

        if (isTransitioning) return;
        stopAutoplay();
        window.gsap?.killTweensOf(track);
        ensureBuffer({ preservePosition: true });
        setTrackX(clampToBounds(getCurrentX() - deltaX));
        updatePositionState();
        ensureBuffer({ preservePosition: true });

        window.clearTimeout(wheelAutoplayTimer);
        wheelAutoplayTimer = window.setTimeout(() => {
          wheelAutoplayTimer = null;
          startAutoplay();
        }, WHEEL_AUTOPLAY_DELAY);
      },
      { passive: false }
    );

    viewport.addEventListener('pointerdown', (event) => {
      if (sourceCount < 2 || isTransitioning || (event.pointerType === 'mouse' && event.button !== 0)) return;

      isDragging = true;
      hasDragged = false;
      dragStartX = event.clientX;
      dragStartTrackX = getCurrentX();
      stopAutoplay();
      window.gsap?.killTweensOf(track);
      track.style.transition = 'none';
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!isDragging) return;

      const delta = event.clientX - dragStartX;
      if (!hasDragged && Math.abs(delta) < 5) return;

      if (!hasDragged) {
        hasDragged = true;
        carousel.classList.add('is-dragging');
      }

      event.preventDefault();
      setTrackX(dragStartTrackX + clampDragDelta(delta));
    });

    const stopDrag = (event) => {
      if (!isDragging) return;

      const delta = event.clientX - dragStartX;
      isDragging = false;

      if (hasDragged) {
        carousel.classList.remove('is-dragging');

        if (Math.abs(delta) >= DRAG_THRESHOLD) {
          setActive(activeIndex + (delta < 0 ? 1 : -1));
        } else {
          applyPosition({ animate: true });
        }
      }

      viewport.releasePointerCapture?.(event.pointerId);
      hasDragged = false;
      startAutoplay();
    };

    viewport.addEventListener('pointerup', stopDrag);
    viewport.addEventListener('pointercancel', stopDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());

    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => applyPosition({ animate: false }), 150);
    });

    applyPosition({ animate: false });
    updateDots();
    startAutoplay();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-nano-setup]').forEach(initSetupCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
